import React, { useCallback, useEffect, useRef, useState } from 'react';

const cssColor = (name, fallback) => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
};

const roundedRect = (ctx, x, y, width, height, radius) => {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, r);
};

// The drawing function always lives in a ref, so pointer movement redraws the
// finished chart without restarting its entrance animation.
const useCanvas = (draw, animationKey) => {
    const canvasRef = useRef(null);
    const drawRef = useRef(draw);
    const redrawRef = useRef(() => {});
    drawRef.current = draw;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        let frame;

        const paint = progress => {
            const rect = canvas.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const pixelWidth = Math.round(rect.width * dpr);
            const pixelHeight = Math.round(rect.height * dpr);
            if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
                canvas.width = pixelWidth;
                canvas.height = pixelHeight;
            }
            const ctx = canvas.getContext('2d');
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, rect.width, rect.height);
            drawRef.current(ctx, rect.width, rect.height, progress);
        };

        const redraw = (animate = false) => {
            cancelAnimationFrame(frame);
            if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                frame = requestAnimationFrame(() => paint(1));
                return;
            }
            let start;
            const render = timestamp => {
                if (!start) start = timestamp;
                const raw = Math.min((timestamp - start) / 500, 1);
                paint(1 - Math.pow(1 - raw, 3));
                if (raw < 1) frame = requestAnimationFrame(render);
            };
            frame = requestAnimationFrame(render);
        };

        redrawRef.current = redraw;
        const resizeObserver = new ResizeObserver(() => redraw(false));
        resizeObserver.observe(canvas);
        const themeObserver = new MutationObserver(() => redraw(false));
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-admin-theme', 'data-admin-color'] });
        redraw(true);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            themeObserver.disconnect();
            redrawRef.current = () => {};
        };
    }, []);

    useEffect(() => { redrawRef.current(true); }, [animationKey]);
    const redraw = useCallback(() => redrawRef.current(false), []);
    return [canvasRef, redraw];
};

const Tooltip = ({ hover, children }) => hover ? (
    <div className="analytics-canvas-tooltip" style={{ left: hover.x, top: hover.y }}>
        {children}
    </div>
) : null;

const CanvasLineChart = ({ data, className }) => {
    const [hover, setHover] = useState(null);
    const draw = useCallback((ctx, width, height, progress) => {
        const text = cssColor('--admin-text-muted', '#94a3b8');
        const grid = cssColor('--admin-border', '#e5e7eb');
        const surface = cssColor('--admin-surface', '#fff');
        const primary = cssColor('--admin-primary', '#b71c1c');
        const violet = '#8b5cf6';
        const pad = { top: 20, right: 16, bottom: 34, left: 34 };
        const chartW = Math.max(width - pad.left - pad.right, 1);
        const chartH = Math.max(height - pad.top - pad.bottom, 1);
        const maxValue = Math.max(1, ...data.flatMap(item => [item.members, item.bookings]));
        const ceiling = Math.max(2, Math.ceil(maxValue / 4) * 4);
        const xFor = index => pad.left + (data.length <= 1 ? chartW / 2 : (chartW / (data.length - 1)) * index);
        const yFor = value => pad.top + chartH - (value / ceiling) * chartH * progress;

        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = 1;
        for (let i = 0; i <= 4; i += 1) {
            const y = pad.top + (chartH / 4) * i;
            ctx.strokeStyle = grid;
            ctx.globalAlpha = 0.75;
            ctx.beginPath();
            ctx.moveTo(pad.left, y);
            ctx.lineTo(width - pad.right, y);
            ctx.stroke();
            ctx.globalAlpha = 1;
            ctx.fillStyle = text;
            ctx.textAlign = 'right';
            ctx.fillText(String(Math.round(ceiling - (ceiling / 4) * i)), pad.left - 8, y);
        }

        const maxLabels = width < 520 ? 5 : 8;
        const labelEvery = Math.max(1, Math.ceil(data.length / maxLabels));
        data.forEach((item, index) => {
            if (index % labelEvery !== 0 && index !== data.length - 1) return;
            ctx.fillStyle = text;
            ctx.textAlign = index === 0 ? 'left' : index === data.length - 1 ? 'right' : 'center';
            ctx.fillText(item.label, xFor(index), height - 12);
        });

        const plot = (key, color, fillArea) => {
            if (!data.length) return;
            if (fillArea) {
                const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + chartH);
                gradient.addColorStop(0, `${color}28`);
                gradient.addColorStop(1, `${color}00`);
                ctx.beginPath();
                data.forEach((item, index) => index ? ctx.lineTo(xFor(index), yFor(item[key])) : ctx.moveTo(xFor(index), yFor(item[key])));
                ctx.lineTo(xFor(data.length - 1), pad.top + chartH);
                ctx.lineTo(xFor(0), pad.top + chartH);
                ctx.closePath();
                ctx.fillStyle = gradient;
                ctx.fill();
            }
            ctx.beginPath();
            data.forEach((item, index) => index ? ctx.lineTo(xFor(index), yFor(item[key])) : ctx.moveTo(xFor(index), yFor(item[key])));
            ctx.strokeStyle = color;
            ctx.lineWidth = 2.5;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.stroke();
        };

        plot('members', primary, true);
        plot('bookings', violet, false);

        if (hover && data[hover.index]) {
            const item = data[hover.index];
            const x = xFor(hover.index);
            ctx.setLineDash([4, 4]);
            ctx.strokeStyle = cssColor('--admin-border-strong', '#cbd5e1');
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, pad.top);
            ctx.lineTo(x, pad.top + chartH);
            ctx.stroke();
            ctx.setLineDash([]);
            [['members', primary], ['bookings', violet]].forEach(([key, color]) => {
                ctx.beginPath();
                ctx.arc(x, yFor(item[key]), 4.5, 0, Math.PI * 2);
                ctx.fillStyle = surface;
                ctx.fill();
                ctx.lineWidth = 2.5;
                ctx.strokeStyle = color;
                ctx.stroke();
            });
        }
    }, [data, hover]);

    const animationKey = data.map(item => `${item.date}:${item.members}:${item.bookings}`).join('|');
    const [ref, redraw] = useCanvas(draw, animationKey);
    useEffect(redraw, [hover, redraw]);

    const onPointerMove = event => {
        if (!data.length) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const chartWidth = Math.max(rect.width - 50, 1);
        const localX = event.clientX - rect.left;
        const index = data.length <= 1 ? 0 : Math.max(0, Math.min(data.length - 1, Math.round(((localX - 34) / chartWidth) * (data.length - 1))));
        setHover({ index, x: Math.max(62, Math.min(rect.width - 62, 34 + (chartWidth / Math.max(data.length - 1, 1)) * index)), y: 8 });
    };

    const item = hover ? data[hover.index] : null;
    return (
        <div className="analytics-canvas-wrap">
            <canvas ref={ref} className={className} role="img" aria-label="Line chart of new members and bookings"
                onPointerMove={onPointerMove} onPointerLeave={() => setHover(null)} />
            <Tooltip hover={hover}>
                <b>{item?.label}</b>
                <span><i style={{ background: 'var(--admin-primary)' }} />Members <strong>{item?.members}</strong></span>
                <span><i style={{ background: '#8b5cf6' }} />Bookings <strong>{item?.bookings}</strong></span>
            </Tooltip>
        </div>
    );
};

const CanvasDonutChart = ({ data, centerLabel, className }) => {
    const [hover, setHover] = useState(null);
    const draw = useCallback((ctx, width, height, progress) => {
        const total = data.reduce((sum, item) => sum + item.value, 0);
        const cx = width / 2;
        const cy = height / 2;
        const radius = Math.min(width, height) * 0.31;
        const baseLineWidth = Math.max(16, radius * 0.3);
        let start = -Math.PI / 2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.lineWidth = baseLineWidth;
        ctx.strokeStyle = cssColor('--admin-surface-muted', '#f3f4f6');
        ctx.stroke();

        if (total) data.forEach((item, index) => {
            if (!item.value) return;
            const sweep = (item.value / total) * Math.PI * 2 * progress;
            ctx.beginPath();
            ctx.arc(cx, cy, radius, start + 0.025, start + sweep - 0.025);
            ctx.lineWidth = baseLineWidth + (hover?.index === index ? 5 : 0);
            ctx.globalAlpha = hover && hover.index !== index ? 0.5 : 1;
            ctx.strokeStyle = item.color === 'primary' ? cssColor('--admin-primary', '#b71c1c') : item.color;
            ctx.stroke();
            ctx.globalAlpha = 1;
            start += sweep;
        });

        ctx.fillStyle = cssColor('--admin-text', '#111827');
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '800 24px Inter, system-ui, sans-serif';
        ctx.fillText((hover ? data[hover.index]?.value : Math.round(total * progress)).toLocaleString('en-IN'), cx, cy - 7);
        ctx.fillStyle = cssColor('--admin-text-muted', '#94a3b8');
        ctx.font = '500 11px Inter, system-ui, sans-serif';
        ctx.fillText(hover ? data[hover.index]?.name : centerLabel, cx, cy + 16);
    }, [data, centerLabel, hover]);

    const animationKey = data.map(item => `${item.name}:${item.value}`).join('|');
    const [ref, redraw] = useCanvas(draw, animationKey);
    useEffect(redraw, [hover, redraw]);

    const onPointerMove = event => {
        const total = data.reduce((sum, item) => sum + item.value, 0);
        if (!total) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const radius = Math.min(rect.width, rect.height) * 0.31;
        const ringWidth = Math.max(16, radius * 0.3);
        const distance = Math.hypot(x - cx, y - cy);
        if (distance < radius - ringWidth || distance > radius + ringWidth) { setHover(null); return; }
        let angle = Math.atan2(y - cy, x - cx) + Math.PI / 2;
        if (angle < 0) angle += Math.PI * 2;
        let accumulated = 0;
        const index = data.findIndex(item => {
            accumulated += (item.value / total) * Math.PI * 2;
            return angle <= accumulated;
        });
        if (index >= 0) setHover({ index, x: Math.max(62, Math.min(rect.width - 62, x)), y: Math.max(4, y - 58) });
    };

    const item = hover ? data[hover.index] : null;
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    return (
        <div className="analytics-canvas-wrap">
            <canvas ref={ref} className={className} role="img" aria-label={`${centerLabel} distribution chart`}
                onPointerMove={onPointerMove} onPointerLeave={() => setHover(null)} />
            <Tooltip hover={hover}>
                <b>{item?.name}</b>
                <span>Count <strong>{item?.value?.toLocaleString('en-IN')}</strong></span>
                <span>Share <strong>{total ? Math.round((item?.value / total) * 100) : 0}%</strong></span>
            </Tooltip>
        </div>
    );
};

const CanvasHorizontalBars = ({ data, className }) => {
    const [hover, setHover] = useState(null);
    const draw = useCallback((ctx, width, height, progress) => {
        const text = cssColor('--admin-text-secondary', '#64748b');
        const muted = cssColor('--admin-surface-muted', '#f3f4f6');
        const primary = cssColor('--admin-primary', '#b71c1c');
        const max = Math.max(1, ...data.map(item => item.value));
        const rowHeight = height / Math.max(data.length, 1);
        const labelWidth = Math.min(112, width * 0.34);
        const valueWidth = 35;
        const barWidth = Math.max(width - labelWidth - valueWidth - 12, 20);
        ctx.textBaseline = 'middle';

        data.forEach((item, index) => {
            const active = hover?.index === index;
            const y = rowHeight * index + rowHeight / 2;
            const h = active ? Math.min(14, rowHeight * 0.4) : Math.min(10, rowHeight * 0.3);
            ctx.fillStyle = active ? cssColor('--admin-text', '#111827') : text;
            ctx.font = `${active ? '700' : '500'} 11px Inter, system-ui, sans-serif`;
            ctx.textAlign = 'left';
            const label = item.name.length > 15 ? `${item.name.slice(0, 14)}…` : item.name;
            ctx.fillText(label, 0, y);
            roundedRect(ctx, labelWidth, y - h / 2, barWidth, h, h / 2);
            ctx.fillStyle = muted;
            ctx.fill();
            const currentWidth = Math.max(2, (item.value / max) * barWidth * progress);
            roundedRect(ctx, labelWidth, y - h / 2, currentWidth, h, h / 2);
            ctx.fillStyle = item.color === 'primary' ? primary : item.color;
            ctx.globalAlpha = hover && !active ? 0.45 : 1;
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.fillStyle = cssColor('--admin-text', '#111827');
            ctx.font = '700 11px Inter, system-ui, sans-serif';
            ctx.textAlign = 'right';
            ctx.fillText(Math.round(item.value * progress).toLocaleString('en-IN'), width, y);
        });
    }, [data, hover]);

    const animationKey = data.map(item => `${item.name}:${item.value}`).join('|');
    const [ref, redraw] = useCanvas(draw, animationKey);
    useEffect(redraw, [hover, redraw]);

    const onPointerMove = event => {
        if (!data.length) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const y = event.clientY - rect.top;
        const index = Math.max(0, Math.min(data.length - 1, Math.floor(y / (rect.height / data.length))));
        setHover({ index, x: Math.max(62, Math.min(rect.width - 62, event.clientX - rect.left)), y: Math.max(4, y - 48) });
    };

    const item = hover ? data[hover.index] : null;
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    return (
        <div className="analytics-canvas-wrap">
            <canvas ref={ref} className={className} role="img" aria-label="Horizontal bar chart"
                onPointerMove={onPointerMove} onPointerLeave={() => setHover(null)} />
            <Tooltip hover={hover}>
                <b>{item?.name}</b>
                <span>Profiles <strong>{item?.value?.toLocaleString('en-IN')}</strong></span>
                <span>Share <strong>{total ? Math.round((item?.value / total) * 100) : 0}%</strong></span>
            </Tooltip>
        </div>
    );
};

export { CanvasDonutChart, CanvasHorizontalBars, CanvasLineChart };
